import { useState } from "react";

interface FAQItem {
  question: string;
  answer: string;
}

interface FAQSectionProps {
  faqs?: FAQItem[];
  title?: string;
}

const defaultFAQs: FAQItem[] = [
  {
    question: "Who provides construction services near me in Colorado Springs?",
    answer:
      "Westec Builders offers professional construction and remodeling services for residential and commercial clients throughout Colorado Springs and the Pikes Peak region. The team handles everything from custom new builds to full renovations with a consistent focus on quality, efficiency, and client satisfaction.",
  },
  {
    question: "How much does home remodeling cost in Colorado?",
    answer:
      "The cost of remodeling depends on project size, scope, materials selected, and complexity of the work. Westec Builders provides detailed, no-obligation estimates after evaluating your space and goals — ensuring accurate pricing, transparent billing, and no unexpected costs throughout the project.",
  },
  {
    question: "How long does a construction project take to complete?",
    answer:
      "Project timelines depend on scope and design complexity. Smaller kitchen or bathroom renovations may take two to six weeks, while full custom home builds typically take four to twelve months. Westec Builders provides a clear, detailed project schedule during the planning phase so you always know what to expect.",
  },
  {
    question: "Is Westec Builders licensed and insured in Colorado?",
    answer:
      "Yes, Westec Builders operates as a fully licensed and insured construction company in Colorado. All work is performed to state and local code requirements, providing homeowners and commercial clients with complete protection and peace of mind throughout every phase of the project.",
  },
  {
    question: "Do you offer free construction estimates?",
    answer:
      "Absolutely. Westec Builders provides free, no-obligation project estimates for all residential and commercial construction work. Simply call the team or submit the online form and a project specialist will be in touch within one to two business days to schedule a consultation.",
  },
  {
    question: "What areas does Westec Builders serve?",
    answer:
      "Westec Builders serves Colorado Springs, Monument, Falcon, Pueblo, Castle Rock, Fountain, Security-Widefield, Peyton, Manitou Springs, Black Forest, Woodland Park, and surrounding Front Range communities — delivering the same quality craftsmanship and professional service at every location.",
  },
  {
    question: "Can I customize my construction or remodeling project?",
    answer:
      "Every project is fully customizable based on your vision, design preferences, functional needs, and budget. The Westec Builders team works collaboratively with you from the initial consultation through material selection and execution to bring your project to life exactly as envisioned.",
  },
];

export default function FAQSection({ faqs, title }: FAQSectionProps) {
  const items = faqs ?? defaultFAQs;
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  const toggle = (i: number) => setOpenIndex(openIndex === i ? null : i);

  return (
    <section className="py-24 bg-white">
      <div className="max-w-4xl mx-auto px-6">
        <div className="text-center mb-14">
          <p className="text-xs font-body text-brand-amber uppercase tracking-widest font-semibold mb-3">
            Common Questions
          </p>
          <h2 className="font-heading font-bold text-4xl lg:text-5xl text-brand-dark leading-tight">
            {title ?? "Frequently Asked Questions"}
          </h2>
          <p className="font-body text-brand-charcoal mt-4 max-w-xl mx-auto text-sm leading-relaxed">
            Everything you need to know about working with Westec Builders — from pricing and timelines to licensing and service areas.
          </p>
        </div>

        <div className="flex flex-col gap-3">
          {items.map((item, i) => (
            <article
              key={i}
              className={`border rounded-xl overflow-hidden transition-colors ${
                openIndex === i ? "border-brand-amber/50 bg-brand-warm" : "border-brand-warm-deep bg-white"
              }`}
            >
              <button
                className="w-full flex items-center justify-between px-6 py-5 text-left cursor-pointer hover:bg-brand-warm transition-colors"
                onClick={() => toggle(i)}
                aria-expanded={openIndex === i}
              >
                <h4 id={`faq-${i}`} className="font-body font-semibold text-brand-dark text-sm pr-4">
                  {item.question}
                </h4>
                <span className="w-7 h-7 flex items-center justify-center flex-shrink-0 bg-brand-warm-mid rounded-full">
                  <i
                    className={`text-brand-amber text-base transition-transform duration-200 ${
                      openIndex === i ? "ri-subtract-line" : "ri-add-line"
                    }`}
                  />
                </span>
              </button>
              {openIndex === i && (
                <div className="px-6 pb-6">
                  <p className="font-body text-sm text-brand-charcoal leading-relaxed">{item.answer}</p>
                </div>
              )}
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}
